import React, { useEffect, useRef, useState } from "react";
import { X, Video as VideoIcon, Square, RotateCcw, Check, AlertCircle } from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";

const MAX_SECONDS = 30;

/**
 * VideoRecorderModal — captures webcam + mic video via MediaRecorder.
 * Enforces a MAX_SECONDS hard cap with countdown. Emits a File(webm) via
 * onCaptured(file) when the user hits "Use this recording".
 *
 * Props:
 *   open       : boolean
 *   onClose    : () => void
 *   onCaptured : (file: File) => void
 */
export default function VideoRecorderModal({ open, onClose, onCaptured }) {
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const liveVideoRef = useRef(null);
  const playbackRef = useRef(null);

  const [status, setStatus] = useState("idle"); // idle | ready | recording | preview | error
  const [error, setError] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [recordedBlobUrl, setRecordedBlobUrl] = useState(null);
  const [recordedFile, setRecordedFile] = useState(null);
  const [facingMode, setFacingMode] = useState("user"); // user | environment

  // Pick a supported webm mimetype (or fallback to mp4 on Safari)
  const pickMime = () => {
    const candidates = [
      "video/webm;codecs=vp9,opus",
      "video/webm;codecs=vp8,opus",
      "video/webm",
      "video/mp4;codecs=h264,aac",
      "video/mp4",
    ];
    for (const m of candidates) {
      if (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(m)) {
        return m;
      }
    }
    return "";
  };

  const cleanupStream = () => {
    try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch (_) { /* noop */ }
    streamRef.current = null;
  };

  const cleanupAll = () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    cleanupStream();
    try { recorderRef.current?.state !== "inactive" && recorderRef.current?.stop(); } catch (_) { /* noop */ }
    recorderRef.current = null;
    chunksRef.current = [];
  };

  const startCamera = async () => {
    setError(null);
    setStatus("idle");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode, width: { ideal: 720 }, height: { ideal: 1280 } },
        audio: true,
      });
      streamRef.current = stream;
      if (liveVideoRef.current) {
        liveVideoRef.current.srcObject = stream;
        liveVideoRef.current.muted = true;
        await liveVideoRef.current.play().catch(() => { /* noop */ });
      }
      setStatus("ready");
    } catch (e) {
      const msg =
        e?.name === "NotAllowedError"
          ? "Camera access was denied. Enable camera & microphone in your browser settings to record."
          : e?.name === "NotFoundError"
          ? "No camera found on this device."
          : e?.message || "Could not start the camera.";
      setError(msg);
      setStatus("error");
    }
  };

  // Start camera when the modal opens or facingMode changes
  useEffect(() => {
    if (!open) return;
    startCamera();
    return () => cleanupAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, facingMode]);

  const startRecording = () => {
    if (!streamRef.current) return;
    chunksRef.current = [];
    const mime = pickMime();
    let rec;
    try {
      rec = new MediaRecorder(streamRef.current, mime ? { mimeType: mime } : undefined);
    } catch (e) {
      toast.error("Recording is not supported on this browser.");
      return;
    }
    recorderRef.current = rec;
    rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
    rec.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: chunksRef.current[0]?.type || mime || "video/webm" });
      const ext = (blob.type.includes("mp4") ? "mp4" : "webm");
      const file = new File([blob], `capture-${Date.now()}.${ext}`, { type: blob.type });
      const url = URL.createObjectURL(blob);
      setRecordedBlobUrl(url);
      setRecordedFile(file);
      setStatus("preview");
      cleanupStream();
    };
    rec.start(250);
    setElapsed(0);
    setStatus("recording");
    timerRef.current = setInterval(() => {
      setElapsed((e) => {
        const next = e + 0.1;
        if (next >= MAX_SECONDS) {
          stopRecording();
          return MAX_SECONDS;
        }
        return next;
      });
    }, 100);
  };

  const stopRecording = () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    try {
      if (recorderRef.current && recorderRef.current.state !== "inactive") {
        recorderRef.current.stop();
      }
    } catch (_) { /* noop */ }
  };

  const retake = () => {
    if (recordedBlobUrl) URL.revokeObjectURL(recordedBlobUrl);
    setRecordedBlobUrl(null);
    setRecordedFile(null);
    setElapsed(0);
    startCamera();
  };

  const use = () => {
    if (!recordedFile) return;
    onCaptured?.(recordedFile);
    // Cleanup — parent modal will close via onClose
    if (recordedBlobUrl) URL.revokeObjectURL(recordedBlobUrl);
    setRecordedBlobUrl(null); setRecordedFile(null); setElapsed(0);
    onClose?.();
  };

  const flipCamera = () => {
    setFacingMode((m) => (m === "user" ? "environment" : "user"));
  };

  if (!open) return null;

  const remaining = Math.max(0, MAX_SECONDS - elapsed);
  const pct = Math.min(100, (elapsed / MAX_SECONDS) * 100);

  return (
    <div className="fixed inset-0 z-[80] bg-black/80 flex items-center justify-center p-0 sm:p-4" data-testid="recorder-backdrop" onClick={() => (status !== "recording" ? onClose?.() : null)}>
      <div
        className="w-full sm:max-w-md bg-card sm:rounded-2xl overflow-hidden shadow-2xl h-full sm:h-auto flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="recorder-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div>
            <div className="label-mono text-muted-foreground text-[10px]">Record video</div>
            <h3 className="font-serif text-xl leading-tight">
              {status === "preview" ? "Review your take" : status === "recording" ? "Recording…" : "Camera"}
            </h3>
          </div>
          <button
            onClick={() => { if (status === "recording") { stopRecording(); } onClose?.(); }}
            className="p-2 rounded-full hover:bg-muted"
            data-testid="recorder-close"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 min-h-0 flex flex-col items-center justify-center bg-black relative">
          {status === "error" ? (
            <div className="text-center text-white p-8">
              <AlertCircle className="w-10 h-10 mx-auto mb-3 text-[hsl(var(--oppose))]" />
              <div className="font-serif text-lg mb-2">Camera unavailable</div>
              <div className="text-sm text-white/80 max-w-xs mx-auto">{error}</div>
              <Button
                onClick={startCamera}
                variant="outline"
                className="mt-4 rounded-full bg-white text-black hover:bg-white/90"
                data-testid="recorder-retry"
              >Try again</Button>
            </div>
          ) : status === "preview" ? (
            <video
              ref={playbackRef}
              src={recordedBlobUrl}
              controls
              autoPlay
              playsInline
              onLoadedMetadata={(e) => {
                // MediaRecorder webm blobs report duration=Infinity. Force the
                // browser to scan the file for its real duration by seeking to a
                // huge timestamp, then rewinding to 0.
                const v = e.currentTarget;
                if (v.duration === Infinity || Number.isNaN(v.duration)) {
                  const onSeeked = () => {
                    v.currentTime = 0;
                    v.removeEventListener("seeked", onSeeked);
                  };
                  v.addEventListener("seeked", onSeeked);
                  try { v.currentTime = 1e10; } catch (_) { /* noop */ }
                }
              }}
              className="w-full max-h-[60vh] object-contain"
              data-testid="recorder-preview"
            />
          ) : (
            <>
              <video
                ref={liveVideoRef}
                autoPlay
                playsInline
                muted
                className="w-full max-h-[60vh] object-contain"
                data-testid="recorder-live"
              />
              {status === "recording" && (
                <>
                  <div className="absolute top-3 left-3 flex items-center gap-2 px-3 py-1.5 rounded-full bg-[hsl(var(--oppose))] text-white text-xs font-bold shadow-md">
                    <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
                    REC
                  </div>
                  <div className="absolute top-3 right-3 px-3 py-1.5 rounded-full bg-black/70 text-white text-xs font-mono">
                    {remaining.toFixed(1)}s left
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/20">
                    <div className="h-full bg-[hsl(var(--oppose))]" style={{ width: `${pct}%` }} />
                  </div>
                </>
              )}
            </>
          )}
        </div>

        {/* Controls */}
        <div className="px-5 py-4 border-t border-border bg-card">
          {status === "preview" ? (
            <div className="flex items-center justify-between gap-3">
              <Button variant="outline" onClick={retake} className="rounded-full flex-1" data-testid="recorder-retake">
                <RotateCcw className="w-4 h-4 mr-1" /> Retake
              </Button>
              <Button
                onClick={use}
                className="rounded-full flex-1 bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90"
                data-testid="recorder-use"
              >
                <Check className="w-4 h-4 mr-1" /> Use this recording
              </Button>
            </div>
          ) : status === "recording" ? (
            <button
              onClick={stopRecording}
              className="mx-auto flex items-center justify-center w-16 h-16 rounded-full bg-[hsl(var(--oppose))] text-white shadow-lg hover:scale-105 transition-transform"
              data-testid="recorder-stop"
              aria-label="Stop recording"
            >
              <Square className="w-6 h-6 fill-current" />
            </button>
          ) : status === "ready" ? (
            <div className="flex items-center justify-between gap-3">
              <button
                onClick={flipCamera}
                className="p-2.5 rounded-full border border-border hover:bg-muted"
                title="Flip camera"
                data-testid="recorder-flip"
              >
                <RotateCcw className="w-5 h-5" />
              </button>
              <button
                onClick={startRecording}
                className="w-16 h-16 rounded-full bg-[hsl(var(--oppose))] text-white shadow-lg flex items-center justify-center hover:scale-105 transition-transform"
                data-testid="recorder-start"
                aria-label="Start recording"
              >
                <div className="w-6 h-6 rounded-full bg-white" />
              </button>
              <div className="w-10 text-right text-[10px] label-mono text-muted-foreground">
                MAX<br/>{MAX_SECONDS}s
              </div>
            </div>
          ) : (
            <div className="text-center text-sm text-muted-foreground flex items-center justify-center gap-2">
              <VideoIcon className="w-4 h-4" /> Starting camera…
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

VideoRecorderModal.MAX_SECONDS = MAX_SECONDS;

// Export a small helper button that the uploader components can drop next to
// their file-picker.
export function RecordButton({ onClick, disabled, testId = "record-video-btn" }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`text-center rounded-xl border border-dashed border-border p-4 hover:bg-secondary/60 transition-colors ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
      data-testid={testId}
    >
      <div className="w-10 h-10 rounded-full bg-[hsl(var(--oppose-soft,var(--primary-soft)))] mx-auto mb-2 flex items-center justify-center">
        <VideoIcon className="w-5 h-5 text-[hsl(var(--oppose))]" />
      </div>
      <div className="font-serif text-base">Record video</div>
      <div className="text-[11px] text-muted-foreground mt-0.5">Use your webcam · max {MAX_SECONDS}s</div>
    </button>
  );
}
